import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { createFilterOptions } from "../../util/Util";
import { useTranslation } from "react-i18next";

type OptionType =
  | "default"
  | "avg_last_x_months"
  | "avg_last_x_years"
  | "avg_last_x_supplies"
  | "avg_date_range";

interface Props {
  onChange: (value: Record<string, any>) => void;
}

const ValueSelector = ({ onChange }: Props) => {
  const { t } = useTranslation("common");
  const [selectedOption, setSelectedOption] = useState<OptionType>("default");
  const [months, setMonths] = useState(1);
  const [years, setYears] = useState(1);
  const [supplies, setSupplies] = useState(1);
  const [dateRange, setDateRange] = useState<{ start: Dayjs | null; end: Dayjs | null }>({ start: null, end: null });

  const options = [
    { label: t("inventory.defaultCostPrice"), value: "default" },
    { label: t("inventory.avgLastXMonths"), value: "avg_last_x_months" },
    { label: t("inventory.avgLastXYears"), value: "avg_last_x_years" },
    { label: t("inventory.avgLastXSupplies"), value: "avg_last_x_supplies" },
    { label: t("inventory.avgDateRange"), value: "avg_date_range" },
  ];

  useEffect(() => {
    const payload: Record<string, any> = { mode: selectedOption };

    if (selectedOption === "avg_last_x_months") payload.months = months;
    if (selectedOption === "avg_last_x_years") payload.years = years;
    if (selectedOption === "avg_last_x_supplies") payload.supplies = supplies;
    if (selectedOption === "avg_date_range") {
      payload.start = dateRange.start?.format("YYYY-MM-DD") || "";
      payload.end = dateRange.end?.format("YYYY-MM-DD") || "";
    }

    onChange(payload);
  }, []);

  return (
    <Box width={600}>
      <Grid container spacing={2}>
        {/* Autocomplete selector */}
        <Grid size="auto" minWidth={150} >
          <Autocomplete
            id="valueInput"
            size="small"
            options={options}
            filterOptions={createFilterOptions((opt) => opt.label || "")}
            getOptionLabel={(option) => option.label}
            value={options.find((opt) => opt.value === selectedOption)}
            onChange={(_, newValue) => {
              setSelectedOption((newValue?.value as OptionType) || "default");
            }}
            autoHighlight
            autoComplete
            autoSelect
            renderInput={(params) => (
              <TextField {...params} label={t("inventory.costPriceMode")} fullWidth />
            )}
            disableClearable
          />
        </Grid>

        {/* Conditional fields */}
        {selectedOption === "avg_last_x_months" && (
          <Grid size="auto">
            <TextField
              type="number"
              label={t("inventory.numberOfMonths")}
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
            />
          </Grid>
        )}

        {selectedOption === "avg_last_x_years" && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              type="number"
              label={t("inventory.numberOfYears")}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </Grid>
        )}

        {selectedOption === "avg_last_x_supplies" && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              type="number"
              label={t("inventory.numberOfSupplies")}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
              value={supplies}
              onChange={(e) => setSupplies(Number(e.target.value))}
            />
          </Grid>
        )}

        {selectedOption === "avg_date_range" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid size={{ xs: 12, md: 3 }}>
              <DatePicker
                label={t("dialog.selectStartDate")}
                value={dateRange.start}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, start: newValue as Dayjs | null }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <DatePicker
                label={t("dialog.selectEndDate")}
                value={dateRange.end}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, end: newValue as Dayjs | null }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
          </LocalizationProvider>
        )}
      </Grid>
    </Box>
  );
};

export default ValueSelector;
